import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import fetchWithAuth from '../utils/fetchWithAuth';

function getAssignmentColor(endDate, isMain) {
  if (isMain) return 'border-green-500 bg-green-50';
  if (!endDate) return 'border-green-500 bg-green-50';
  const today = new Date();
  const end = new Date(endDate);
  const diffDays = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
  if (diffDays <= 1) return 'border-red-500 bg-red-50';
  if (diffDays <= 5 && diffDays >= 2) return 'border-yellow-500 bg-yellow-50';
  if (diffDays > 5) return 'border-green-500 bg-green-50';
  return '';
}

const AssignmentList = ({ drivers, refresh, isConductor }) => {
  const [assignments, setAssignments] = useState([]);
  const [showFinished, setShowFinished] = useState(false);
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [driverFilter, setDriverFilter] = useState('');
  const [mainOnly, setMainOnly] = useState(false);

  useEffect(() => {
    fetchWithAuth(`${BACKEND_URL}/assignments?showFinished=${showFinished}`)
      .then(res => res.json())
      .then(data => setAssignments(data));
  }, [showFinished, refresh]);

  // Helper to get driver name by license
  const getDriverName = (license) => {
    const driver = drivers?.find(d => d.license === license);
    return driver ? `${driver.name} (${license})` : license;
  };

  // Get unique vehicle plates and driver licenses for filters
  const vehiclePlates = [...new Set(assignments.map(a => a.vehiclePlate))];
  const driverLicenses = [...new Set(assignments.map(a => a.driverLicense))];

  // Apply UI filters
  const filteredAssignments = assignments.filter(a =>
    (vehicleFilter ? a.vehiclePlate === vehicleFilter : true) &&
    (driverFilter ? a.driverLicense === driverFilter : true) &&
    (!mainOnly || a.isMain)
  );

  // If conductor, show cards instead of table
  if (isConductor) {
    return (
      <div>
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
            onClick={() => setShowFinished(f => !f)}
          >
            {showFinished ? "Mostrar asignaciones activas" : "Mostrar todas las asignaciones"}
          </button>
          <select
            className="px-2 py-1 border rounded"
            value={vehicleFilter}
            onChange={e => setVehicleFilter(e.target.value)}
          >
            <option value="">Todos los vehículos</option>
            {vehiclePlates.map(plate => (
              <option key={plate} value={plate}>{plate}</option>
            ))}
          </select>
          <select
            className="px-2 py-1 border rounded"
            value={driverFilter}
            onChange={e => setDriverFilter(e.target.value)}
          >
            <option value="">Todos los conductores</option>
            {driverLicenses.map(lic => (
              <option key={lic} value={lic}>{getDriverName(lic)}</option>
            ))}
          </select>
          <label className="flex items-center space-x-1">
            <input
              type="checkbox"
              checked={mainOnly}
              onChange={e => setMainOnly(e.target.checked)}
            />
            <span>Solo principal</span>
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAssignments.map(a => {
            // Calculate days left
            let daysLeft = null;
            if (!a.isMain && a.endDate) {
              const today = new Date();
              const end = new Date(a.endDate);
              daysLeft = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
            }
            return (
            <div
              key={a._id}
              className={`border-2 rounded-xl shadow-md p-6 flex flex-col ${getAssignmentColor(a.endDate, a.isMain)}`}
            >
              <div className="flex items-center mb-2">
                <div className="flex-1">
                  <div className="font-semibold text-lg mb-1">
                    Vehículo: <span className="font-normal">{a.vehiclePlate}</span>
                  </div>
                  <div className="text-gray-700 text-sm mb-1">
                    <span className="font-medium">Conductor:</span> {getDriverName(a.driverLicense)}
                  </div>
                  <div className="text-gray-700 text-sm mb-1">
                    <span className="font-medium">Inicio:</span> {a.startDate}
                  </div>
                  <div className="text-gray-700 text-sm mb-1">
                    <span className="font-medium">Fin:</span> {a.endDate || '-'}
                  </div>
                  <div className="text-gray-700 text-sm mb-1">
                    <span className="font-medium">Notas:</span> {a.notes}
                  </div>
                  <div className="text-gray-700 text-sm mb-1">
                    <span className="font-medium">AsignaciónPrincipal:</span> {a.isMain ? "Sí" : "No"}
                  </div>
                </div>
              </div>
                {/* Note for days left if not main */}
                {!a.isMain && a.endDate && (
                  <div className={`mt-4 border-t-2 pt-2 text-sm font-semibold text-center ${getAssignmentColor(a.endDate, a.isMain)}`}>
                    {daysLeft > 1
                      ? `Quedan ${daysLeft} días para el fin de la asignación`
                      : daysLeft === 1
                        ? 'Queda 1 día para el fin de la asignación'
                        : daysLeft === 0
                          ? 'La asignación termina hoy'
                          : 'La asignación ha finalizado'}
                  </div>
                )}
            </div>
          );
          })}
          {filteredAssignments.length === 0 && (
            <div className="col-span-full text-center text-gray-500 py-8">
              No hay asignaciones para mostrar.
            </div>
          )}
        </div>
      </div>
    );
  }

  // Default: show table for non-conductor roles
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          onClick={() => setShowFinished(f => !f)}
        >
          {showFinished ? "Mostrar asignaciones activas" : "Mostrar todas las asignaciones"}
        </button>
        <select
          className="px-2 py-1 border rounded"
          value={vehicleFilter}
          onChange={e => setVehicleFilter(e.target.value)}
        >
          <option value="">Todos los vehículos</option>
          {vehiclePlates.map(plate => (
            <option key={plate} value={plate}>{plate}</option>
          ))}
        </select>
        <select
          className="px-2 py-1 border rounded"
          value={driverFilter}
          onChange={e => setDriverFilter(e.target.value)}
        >
          <option value="">Todos los conductores</option>
          {driverLicenses.map(lic => (
            <option key={lic} value={lic}>{getDriverName(lic)}</option>
          ))}
        </select>
        <label className="flex items-center space-x-1">
          <input
            type="checkbox"
            checked={mainOnly}
            onChange={e => setMainOnly(e.target.checked)}
          />
          <span>Solo principal</span>
        </label>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded shadow">
          <thead>
            <tr>
              <th className="px-4 py-2">Vehículo</th>
              <th className="px-4 py-2">Conductor</th>
              <th className="px-4 py-2">Inicio</th>
              <th className="px-4 py-2">Fin</th>
              <th className="px-4 py-2">Notas</th>
              <th className="px-4 py-2">Principal</th>
              {!isConductor && <th className="px-4 py-2">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {filteredAssignments.map(a => (
              <tr key={a._id}>
                <td className="px-4 py-2">{a.vehiclePlate}</td>
                <td className="px-4 py-2">{getDriverName(a.driverLicense)}</td>
                <td className="px-4 py-2">{a.startDate}</td>
                <td className="px-4 py-2">{a.endDate || '-'}</td>
                <td className="px-4 py-2">{a.notes}</td>
                <td className="px-4 py-2">{a.isMain ? "Sí" : "No"}</td>
                {!isConductor && (
                  <td className="px-4 py-2">
                    {/* Place edit/delete buttons here if needed */}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AssignmentList;